import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Wrap,
  WrapItem,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { IoClose } from 'react-icons/io5';
import xhr from 'xhr';

const defaultValue = {
  // Ojo: mantenemos program_type “interno” para que tu endpoint siga igual
  program_type: '',
  program: '',
  group: '',
  status: 'estudiando', // fijo
  semester: '',
};

// Tus dos program types permitidos
const ALLOWED_PROGRAM_TYPE_IDS = [1, 35]; // bachillerato anual (1) y primaria anual (35)

function SmartFilterBarTeach({
  title = 'Filtro',
  description = '',
  value = defaultValue,
  onChange,
  loading = false,
}) {
  const [programTypes, setProgramTypes] = useState([]); // solo los 2 permitidos
  const [grades, setGrades] = useState([]); // mezclados
  const [groups, setGroups] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [fetching, setFetching] = useState(true);

  // Para poder deducir qué program_type corresponde al grade elegido
  const gradeToProgramTypeSlug = useMemo(() => {
    const map = {};
    grades.forEach((g) => {
      map[Number(g.value)] = g.program_type_slug; // guardado en cada grade
    });
    return map;
  }, [grades]);

  const handleChange = (field, val) => {
    const next = { ...value, status: 'estudiando' }; // siempre fijo

    if (field === 'program') {
      next.program = val;
      next.group = '';

      // auto-ponemos program_type según el grade escogido
      const ptSlug = gradeToProgramTypeSlug[Number(val)];
      next.program_type = ptSlug || '';
    } else if (field === 'group') {
      next.group = val;
    } else if (field === 'semester') {
      next.semester = val;
    }

    onChange(next);
  };

  const chipStyles = {
    borderRadius: 'lg',
    px: 3,
    py: 1.5,
    cursor: 'pointer',
    borderWidth: '1px',
    borderColor: 'gray.200',
    bg: 'white',
    _hover: { bg: 'gray.50' },
  };

  const labelStyles = { as: 'span', color: 'gray.600', fontSize: 'sm', mr: 2 };
  const valueStyles = { as: 'span', fontWeight: 'medium', fontSize: 'sm', color: 'gray.900' };

  const renderChip = (label, displayVal, menuContent, isDisabled = false, field = null) => {
    const hasValue = field && value[field] !== '' && value[field] != null;
    return (
      <Flex
        key={label}
        align="center"
        {...chipStyles}
        opacity={loading || fetching || isDisabled ? 0.7 : 1}
        position="relative"
      >
        <Menu placement="bottom-start">
          <MenuButton
            as={Box}
            position="absolute"
            inset={0}
            cursor={loading || fetching || isDisabled ? 'not-allowed' : 'pointer'}
            bg="transparent"
            border="none"
            borderRadius="inherit"
            isDisabled={loading || fetching || isDisabled}
            _hover={{}}
            _active={{}}
          />
          <MenuList minW="220px" zIndex={10}>{menuContent}</MenuList>
        </Menu>

        <Flex align="center" gap={0} w="100%" pointerEvents="none">
          <Text {...labelStyles}>{label}:</Text>
          <Flex
            align="center"
            bg="gray.100"
            borderRadius="full"
            pl={hasValue ? 1 : 2.5}
            pr={2.5}
            py={0.5}
            gap={1}
          >
            {hasValue && (
              <Box
                as="button"
                type="button"
                borderRadius="full"
                p={0.5}
                color="gray.500"
                _hover={{ bg: 'gray.200', color: 'gray.700' }}
                onClick={() => handleChange(field, '')}
                aria-label={`Limpiar ${label}`}
                flexShrink={0}
                pointerEvents="auto"
                position="relative"
                zIndex={1}
              >
                <Box as={IoClose} boxSize={4} />
              </Box>
            )}
            <Text {...valueStyles}>{displayVal || '—'}</Text>
          </Flex>
          <ChevronDownIcon boxSize={4} ml={2} color="gray.500" flexShrink={0} />
        </Flex>
      </Flex>
    );
  };

  // Load program types (solo 1 y 35)
  useEffect(() => {
    const load = async () => {
      setFetching(true);
      try {
        const res = await xhr(`/program-types?filters[active][$eq]=true`);
        const data = res?.data?.data || [];

        const allowed = data.filter((pt) => ALLOWED_PROGRAM_TYPE_IDS.includes(pt.id));
        // pt: {id, name, type, slug}
        setProgramTypes(allowed.map((pt) => ({ ...pt, label: pt.name, value: pt.slug })));
      } finally {
        setFetching(false);
      }
    };
    load();
  }, []);

  // Load semesters
  useEffect(() => {
    const loadSemesters = async () => {
      try {
        const res = await xhr(`/semesters?pagination[limit]=-1&filters[$or][0][school][id][$null]=true&filters[$or][1][school][id][$eq]=0`);
        setSemesters((res?.data?.data || []).map((s) => ({ ...s, label: s.name, value: s.id })));
      } catch {
        setSemesters([]);
      }
    };
    loadSemesters();
  }, []);

  // Load ALL grades from both program types and mix them
  useEffect(() => {
    const loadGrades = async () => {
      if (!programTypes.length) return;

      setGrades([]);
      setGroups([]);
      setFetching(true);

      try {
        // Como type es "grade", endpoint es /grades
        const responses = await Promise.all(
          programTypes.map((pt) =>
            xhr(`/grades?filters[program_type][id][$eq]=${pt.id}&filters[active][$eq]=true`)
              .then((r) => ({ pt, list: r?.data?.data || [] }))
              .catch(() => ({ pt, list: [] }))
          )
        );

        const merged = [];
        responses.forEach(({ pt, list }) => {
          list.forEach((g) => {
            merged.push({
              ...g,
              label: g.name,
              value: g.id,
              // guardamos de qué program_type viene para reconstruir el filtro
              program_type_id: pt.id,
              program_type_slug: pt.slug,
              program_type_name: pt.name,
            });
          });
        });

        // Ordenar por nombre para que quede “mezclado” bonito
        merged.sort((a, b) => String(a.label).localeCompare(String(b.label), 'es'));

        setGrades(merged);

        // Si no hay program_type aún, pero ya hay value.program seleccionado desde URL, setear program_type automáticamente
        if (value.program && !value.program_type) {
          const found = merged.find((x) => Number(x.value) === Number(value.program));
          if (found?.program_type_slug) {
            onChange({ ...value, status: 'estudiando', program_type: found.program_type_slug });
          }
        }
      } finally {
        setFetching(false);
      }
    };

    loadGrades();

  }, [programTypes]);

  // Load groups by selected grade (program)
  const getGroupsByGrade = useCallback(async (gradeId) => {
    if (!gradeId) return;
    setGroups([]);
    try {
      const res = await xhr(`/class-groups?filters[grade][id][$eq]=${gradeId}&filters[active][$eq]=true&filters[$or][0][school][id][$null]=true&filters[$or][1][school][id][$eq]=0`);
      setGroups(
        (res?.data?.data || []).map((el) => ({
          ...el,
          label: el.name && el.name !== '.' ? el.name : `Grupo ${el.id}`,
          value: el.id,
        }))
      );
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    if (value.program) getGroupsByGrade(Number(value.program));
    else setGroups([]);
  }, [value.program, getGroupsByGrade]);

  const gradeLabel = grades.find((g) => Number(g.value) === Number(value.program))?.label || value.program || '—';
  const groupLabel = groups.find((g) => Number(g.value) === Number(value.group))?.label || value.group || '—';
  const semesterLabel = semesters.find((s) => Number(s.value) === Number(value.semester))?.label || value.semester || '—';

  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" p={5}>
      {(title || description) && (
        <Flex align="baseline" mb={4} columnGap={2} rowGap={1} flexWrap="wrap">
          {title && (
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              {title}
            </Text>
          )}
          {description && (
            <Text fontSize="sm" color="gray.600">
              {description}
            </Text>
          )}
        </Flex>
      )}

      {fetching ? (
        <Flex align="center" gap={2} py={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">Cargando filtros...</Text>
        </Flex>
      ) : (
        <Wrap spacing={3} align="center">
          <WrapItem>
            {renderChip(
              'Grado',
              gradeLabel,
              grades.length
                ? grades.map((g) => (
                  <MenuItem key={g.value} onClick={() => handleChange('program', g.value)}>
                    {g.label}
                  </MenuItem>
                ))
                : [<MenuItem key="empty" isDisabled>No hay grados disponibles</MenuItem>],
              false,
              'program'
            )}
          </WrapItem>

          <WrapItem>
            {renderChip(
              'Grupo',
              groupLabel,
              groups.length
                ? groups.map((g) => (
                  <MenuItem key={g.value} onClick={() => handleChange('group', g.value)}>
                    {g.label}
                  </MenuItem>
                ))
                : [<MenuItem key="empty" isDisabled>Selecciona primero un Grado</MenuItem>],
              !value.program,
              'group'
            )}
          </WrapItem>

          <WrapItem>
            {renderChip(
              'Semestre',
              semesterLabel,
              semesters.map((s) => (
                <MenuItem key={s.value} onClick={() => handleChange('semester', s.value)}>
                  {s.label}
                </MenuItem>
              )),
              false,
              'semester'
            )}
          </WrapItem>
        </Wrap>
      )}
    </Box>
  );
}

export default SmartFilterBarTeach;